import { Component, inject, signal, OnInit } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { CardModule } from 'primeng/card';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { MessageModule } from 'primeng/message';
import { MiddlewareApi } from '../../core/services/middleware-api.service';
import { OnboardedLocation } from '../../core/models';

// Location list — each row navigates to the dedicated management page (/locations/:locationId).
// Deliberately NOT a modal: location management (details, taxes, payment methods) needs room.
@Component({
    selector: 'app-locations',
    standalone: true,
    imports: [CardModule, ButtonModule, TableModule, TagModule, MessageModule, RouterLink],
    template: `
        <p-card>
            <ng-template pTemplate="title">
                <div class="flex items-center gap-2">
                    <span>Locations</span>
                    @if (instance()) {
                        <p-tag [value]="instance()!" severity="info" />
                    }
                </div>
            </ng-template>

            @if (!instance() && !loading() && !error()) {
                <p-message severity="warn" styleClass="mb-3">No active Magento instance — enable one under Magento Instances.</p-message>
            }

            <p-table [value]="locations()" [loading]="loading()" [paginator]="locations().length > 15" [rows]="15">
                <ng-template pTemplate="header">
                    <tr>
                        <th>Location ID</th>
                        <th>Name</th>
                        <th>Merchant</th>
                        <th>Store Code</th>
                        <th>Store View</th>
                        <th>Instant Print</th>
                        <th></th>
                    </tr>
                </ng-template>
                <ng-template pTemplate="body" let-loc>
                    <tr class="cursor-pointer" (click)="open(loc)">
                        <td class="font-medium">{{ loc.locationId }}</td>
                        <td>{{ loc.name ?? '—' }}</td>
                        <td>
                            @if (loc.merchantId) {
                                <a class="text-primary hover:underline" [routerLink]="['/merchants', loc.merchantId]"
                                   (click)="$event.stopPropagation()">{{ loc.merchantName ?? loc.merchantId }}</a>
                            } @else {
                                <span class="text-surface-400">Not under a merchant</span>
                            }
                        </td>
                        <td>{{ loc.magentoCode }}</td>
                        <td>
                            @if (instanceBaseUrl() && loc.magentoCode) {
                                <a class="text-primary hover:underline" target="_blank" rel="noopener"
                                   (click)="$event.stopPropagation()"
                                   [href]="instanceBaseUrl() + '/' + loc.magentoCode + '/'">
                                    {{ loc.storeViewName ?? loc.magentoStoreViewId }} ↗
                                </a>
                            } @else {
                                {{ loc.storeViewName ?? loc.magentoStoreViewId }}
                            }
                        </td>
                        <td>
                            @if (loc.isInstantPrintEnabled == null) {
                                <span class="text-surface-400">—</span>
                            } @else {
                                <p-tag [value]="loc.isInstantPrintEnabled ? 'On' : 'Off'" [severity]="loc.isInstantPrintEnabled ? 'success' : 'secondary'" />
                            }
                        </td>
                        <td><p-button icon="pi pi-arrow-right" [text]="true" (onClick)="open(loc)" /></td>
                    </tr>
                </ng-template>
                <ng-template pTemplate="emptymessage">
                    <tr>
                        <td colspan="7">{{ error() ?? 'No locations onboarded in the active instance yet.' }}</td>
                    </tr>
                </ng-template>
            </p-table>
        </p-card>
    `
})
export class Locations implements OnInit {
    private readonly api = inject(MiddlewareApi);
    private readonly router = inject(Router);

    readonly locations = signal<OnboardedLocation[]>([]);
    readonly instance = signal<string | null>(null);
    // The active instance's storefront base — what makes the Store View column a real door:
    // {baseUrl}/{storeCode}/ opens the location's own shop.
    readonly instanceBaseUrl = signal<string | null>(null);
    readonly loading = signal(true);
    readonly error = signal<string | null>(null);

    ngOnInit(): void {
        this.api.getLocations().subscribe({
            next: (r) => {
                this.locations.set(r.locations ?? []);
                this.instance.set(r.instance ?? null);
                this.instanceBaseUrl.set(r.instanceBaseUrl?.replace(/\/+$/, '') ?? null);
                this.loading.set(false);
            },
            error: (e) => {
                this.error.set(this.describe(e));
                this.loading.set(false);
            }
        });
    }

    open(loc: OnboardedLocation): void {
        this.router.navigate(['/locations', loc.locationId]);
    }

    private describe(e: any): string {
        if (e?.status === 0) return 'Cannot reach the API.';
        if (e?.status === 401) return 'Unauthorized — signing in…';
        if (e?.status === 403) return 'Forbidden — your role lacks the merchants:read permission.';
        return `Error ${e?.status ?? ''}: ${e?.message ?? 'request failed'}`;
    }
}
